/**
 * DunningBanner — global past-due payment nudge (design Screen 6).
 *
 * Renders a full-bleed amber banner above the app content whenever the current
 * user's subscription `status === 'past_due'`. Empathetic, recoverable framing
 * (amber/warning, never red): a failed charge is a fixable nudge, not a security
 * alert. Hidden entirely for any other status (including while loading and on
 * any billing-API error — we fail silent rather than show billing noise).
 *
 * Status is read via `useSubscription({ pollMs: 60000 })`: when a webhook flips
 * the subscription back to `active` (card updated), the banner unmounts within
 * ~60s without a page reload. Mounted once in the App shell so it appears on
 * every authenticated page.
 *
 * Accessibility: `role="status"` + `aria-live="polite"` (a recoverable nudge,
 * not an assertive alert), and a real keyboard-focusable button for the CTA.
 *
 * @param {object} props
 * @param {() => void} [props.onUpdatePayment] - Invoked when the user clicks the
 *   CTA. In the app shell this navigates to the billing/pricing surface. If
 *   omitted, the CTA falls back to navigating to {@link UPDATE_PAYMENT_URL}.
 */

import React from 'react';
import { useSubscription } from '../../hooks/useSubscription';
import styles from './DunningBanner.module.css';

const POLL_MS = 60000;

// The billing settings page (PR4) is the canonical "manage your plan"
// destination. The app shell passes an `onUpdatePayment` that navigates to the
// in-app billing tab; this URL is the fallback for standalone renders.
export const UPDATE_PAYMENT_URL = '/billing';

export default function DunningBanner({ onUpdatePayment }) {
  const { subscription } = useSubscription({ pollMs: POLL_MS });

  // Only render in the past_due state. Loading, error, and every other status
  // (active, trialing, canceled, null) render nothing.
  if (subscription?.status !== 'past_due') {
    return null;
  }

  const handleUpdate = () => {
    if (onUpdatePayment) {
      onUpdatePayment();
      return;
    }
    window.location.assign(UPDATE_PAYMENT_URL);
  };

  return (
    <div
      className={styles.banner}
      role="status"
      aria-live="polite"
      data-testid="dunning-banner"
    >
      <span className={styles.icon} aria-hidden="true">
        {/* Amber warning triangle — current-color so it inherits the text tone. */}
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
          <path d="M12 9v4" />
          <path d="M12 17h.01" />
        </svg>
      </span>
      <p className={styles.message}>
        We couldn't process your last payment. Update your payment method to keep
        your plan active.
      </p>
      <button
        type="button"
        className={styles.cta}
        onClick={handleUpdate}
        data-testid="dunning-update-payment"
      >
        Update payment
      </button>
    </div>
  );
}
